import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { api, ApiError, login, register } from "../api/client";
import { useT } from "../i18n";

function useErrorText() {
  const t = useT();
  return (e: unknown) => {
    if (e instanceof ApiError) {
      if (e.status === 422) return t(e.detail.includes("email") ? "err.invalid_email" : "err.invalid_input");
      const key = `err.${e.detail}`;
      const text = t(key);
      return text === key ? t("err.generic") : text;
    }
    return t("err.generic");
  };
}

export function Login() {
  const t = useT();
  const nav = useNavigate();
  const qc = useQueryClient();
  const errorText = useErrorText();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(email, password);
      qc.clear();
      nav("/");
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <main className="page narrow">
      <form className="card stack" onSubmit={submit}>
        <h1>{t("auth.login")}</h1>
        <label>{t("auth.email")}<input type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} /></label>
        <label>{t("auth.password")}<input type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} /></label>
        {error && <p className="err small">{error}</p>}
        <button className="primary" disabled={busy}>{t("auth.login")}</button>
        <div className="row small" style={{ justifyContent: "space-between" }}>
          <Link to="/forgot">{t("auth.forgot")}</Link>
          <span className="muted">{t("auth.no_account")} <Link to="/register">{t("nav.signup")}</Link></span>
        </div>
      </form>
    </main>
  );
}

export function Register() {
  const t = useT();
  const nav = useNavigate();
  const qc = useQueryClient();
  const errorText = useErrorText();
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await register(form.email, form.password, form.name);
      qc.clear();
      nav("/train");
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <main className="page narrow">
      <form className="card stack" onSubmit={submit}>
        <h1>{t("auth.signup")}</h1>
        <label>{t("auth.name")}<input required minLength={2} maxLength={40} autoComplete="nickname" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
        <label>{t("auth.email")}<input type="email" required autoComplete="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></label>
        <label>{t("auth.password")}<input type="password" required minLength={8} autoComplete="new-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></label>
        {error && <p className="err small">{error}</p>}
        <button className="primary" disabled={busy}>{t("auth.signup")}</button>
        <p className="small muted">{t("auth.have_account")} <Link to="/login">{t("nav.login")}</Link></p>
      </form>
    </main>
  );
}

export function Verify() {
  const t = useT();
  const [params] = useSearchParams();
  const [state, setState] = useState<"busy" | "ok" | "bad">("busy");
  useEffect(() => {
    const token = params.get("token");
    if (!token) return setState("bad");
    api("/auth/verify", { body: { token } }).then(() => setState("ok"), () => setState("bad"));
  }, [params]);
  return (
    <main className="page narrow">
      <div className="card">
        {state === "busy" ? t("common.loading") : state === "ok" ? <p className="ok">{t("auth.verified")}</p> : <p className="err">{t("auth.verify_failed")}</p>}
        <Link to="/">→ {t("app.name")}</Link>
      </div>
    </main>
  );
}

export function Forgot() {
  const t = useT();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  async function submit(e: FormEvent) {
    e.preventDefault();
    await api("/auth/password/forgot", { body: { email } }).catch(() => {});
    setSent(true);
  }
  return (
    <main className="page narrow">
      <form className="card stack" onSubmit={submit}>
        <h1>{t("auth.forgot")}</h1>
        {sent ? (
          <p>{t("auth.reset_sent")}</p>
        ) : (
          <>
            <label>{t("auth.email")}<input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} /></label>
            <button className="primary">{t("auth.send_reset")}</button>
          </>
        )}
      </form>
    </main>
  );
}

export function Reset() {
  const t = useT();
  const [params] = useSearchParams();
  const [password, setPassword] = useState("");
  const [state, setState] = useState<"idle" | "ok" | "bad">("idle");
  async function submit(e: FormEvent) {
    e.preventDefault();
    try {
      await api("/auth/password/reset", { body: { token: params.get("token") ?? "", password } });
      setState("ok");
    } catch {
      setState("bad");
    }
  }
  return (
    <main className="page narrow">
      <form className="card stack" onSubmit={submit}>
        <h1>{t("auth.new_password")}</h1>
        {state === "ok" ? (
          <p className="ok">{t("auth.reset_done")} <Link to="/login">{t("nav.login")}</Link></p>
        ) : (
          <>
            <label>{t("auth.new_password")}<input type="password" required minLength={8} autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} /></label>
            {state === "bad" && <p className="err small">{t("auth.verify_failed")}</p>}
            <button className="primary">{t("auth.set_password")}</button>
          </>
        )}
      </form>
    </main>
  );
}
